/**
 * Main configuration schema aggregation
 */

import { z } from "zod";
import { CameraConfigSchema } from "./camera.schema";
import { EngineConfigSchema } from "./engine.schema";
import { LightConfigSchema } from "./light.schema";
import { MaterialConfigSchema } from "./material.schema";
import { MeshConfigSchema } from "./mesh.schema";
import { SceneManagerConfigSchema, SceneOptionsSchema } from "./scene.schema";

/**
 * Entity configuration - a union of all entity types
 */
export const EntityConfigSchema = z.union([
  CameraConfigSchema,
  LightConfigSchema,
  MeshConfigSchema,
  MaterialConfigSchema,
]);

/**
 * Main Babylon configuration schema
 */
export const BabylonConfigSchema = z.object({
  /**
   * Debug mode flag
   */
  debug: z.boolean().optional(),

  /**
   * Engine configuration
   */
  engine: EngineConfigSchema,

  /**
   * Feature flags
   */
  features: z
    .object({
      enableAR: z.boolean().optional(),
      enableAudio: z.boolean().optional(),
      enablePhysics: z.boolean().optional(),
      enablePostProcessing: z.boolean().optional(),
      enableShadows: z.boolean().optional(),
      enableVR: z.boolean().optional(),
    })
    .optional(),

  /**
   * Initial entities to create in the scene
   */
  initialEntities: z
    .object({
      cameras: z.array(CameraConfigSchema).optional(),
      lights: z.array(LightConfigSchema).optional(),
      materials: z.array(MaterialConfigSchema).optional(),
      meshes: z.array(MeshConfigSchema).optional(),
    })
    .optional(),

  /**
   * Application metadata
   */
  metadata: z
    .object({
      author: z.string().optional(),
      description: z.string().optional(),
      name: z.string().optional(),
      version: z.string().optional(),
    })
    .optional(),

  /**
   * Performance settings
   */
  performance: z
    .object({
      adaptiveQuality: z.boolean().optional(),
      hardwareScalingLevel: z.number().min(0.25).max(2).optional(),
      maxTextureSize: z.number().positive().optional(),
      targetFPS: z.number().positive().optional(),
      useWorkers: z.boolean().optional(),
    })
    .optional(),

  /**
   * Scene configuration
   */
  scene: SceneOptionsSchema.optional(),

  /**
   * Scene manager configuration
   */
  sceneManager: SceneManagerConfigSchema.optional(),
});

/**
 * Partial configuration for updates
 */
export const PartialBabylonConfigSchema = BabylonConfigSchema.partial();

/**
 * Inferred types
 */
export type EntityConfig = z.infer<typeof EntityConfigSchema>;
export type BabylonConfig = z.infer<typeof BabylonConfigSchema>;
export type PartialBabylonConfig = z.infer<typeof PartialBabylonConfigSchema>;

/**
 * Default configuration (separate from schema as per CLAUDE.md)
 */
export const defaultBabylonConfig: BabylonConfig = {
  engine: {
    alpha: false,
    antialias: true,
    audioEngine: true,
    depth: true,
    deterministicLockstep: false,
    doNotHandleContextLost: false,
    failIfMajorPerformanceCaveat: false,
    lockstepMaxSteps: 4,
    powerPreference: "default",
    premultipliedAlpha: true,
    preserveDrawingBuffer: false,
    stencil: true,
    type: "webgl",
    useHighPrecisionFloats: true,
    useHighPrecisionMatrix: false,
    xrCompatible: false,
  },
  features: {
    enableAR: false,
    enableAudio: true,
    enablePhysics: false,
    enablePostProcessing: false,
    enableShadows: true,
    enableVR: false,
  },
  initialEntities: {
    cameras: [],
    lights: [],
    materials: [],
    meshes: [],
  },
  metadata: {
    description: "A Babylon.js application",
    name: "Babylon Application",
    version: "1.0.0",
  },
  performance: {
    adaptiveQuality: false,
    hardwareScalingLevel: 1,
    maxTextureSize: 4096,
    targetFPS: 60,
    useWorkers: false,
  },
  scene: {
    ambientColor: { b: 1, g: 1, r: 1 },
    clearColor: { a: 1, b: 0.3, g: 0.2, r: 0.2 },
    collisionsEnabled: false,
    enableOptimizer: false,
    enablePhysics: false,
    fogColor: { b: 0.3, g: 0.2, r: 0.2 },
    fogDensity: 0.01,
    fogEnd: 60,
    fogMode: "none",
    fogStart: 20,
    gravity: { x: 0, y: -9.81, z: 0 },
  },
  sceneManager: {
    autoOptimize: false,
    debug: false,
    enableDebugLayer: false,
    targetFps: 60,
  },
};

/**
 * Validation helpers
 */
export function validateBabylonConfig(input: unknown): BabylonConfig {
  return BabylonConfigSchema.parse(input);
}

export function safeValidateBabylonConfig(
  input: unknown,
):
  | { success: true; data: BabylonConfig }
  | { success: false; error: z.ZodError } {
  const result = BabylonConfigSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}

export function validatePartialBabylonConfig(
  input: unknown,
): PartialBabylonConfig {
  return PartialBabylonConfigSchema.parse(input);
}

export function safeValidatePartialBabylonConfig(
  input: unknown,
):
  | { success: true; data: PartialBabylonConfig }
  | { success: false; error: z.ZodError } {
  const result = PartialBabylonConfigSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}

export * from "./camera.schema";
/**
 * Re-export all schemas and types
 */
export * from "./engine.schema";
export * from "./light.schema";
export * from "./material.schema";
export * from "./mesh.schema";
export * from "./scene.schema";
